import type { Entity } from '@/types/Entity';
import {
  buildPersonSizingImportFromSheet,
  parsePersonSizingImportSheet,
  type ImportedSizingDraft,
  type ParsedSheetRow,
  type PersonSizingImportSheet,
} from './personSizingImport';

const INDIVIDUAL_ID_ALIASES = [
  'individual id',
  'individual_id',
  'person id',
];

const normalizeHeader = (value: string): string =>
  value.trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');

export const normalizeIndividualId = (value: string): string =>
  value
    .trim()
    .toUpperCase()
    .replace(/^INDV[-\s]*/, '')
    .replace(/\s+/g, '');

const findIndividualIdColumn = (headers: string[], individualIdLabel?: string): number => {
  const normalizedHeaders = headers.map(normalizeHeader);
  const aliases = [...INDIVIDUAL_ID_ALIASES];
  if (individualIdLabel) aliases.push(individualIdLabel);
  const normalizedAliases = aliases.map(normalizeHeader);
  return normalizedHeaders.findIndex((header) => normalizedAliases.includes(header));
};

const getColumnLabel = (headers: string[], index: number): string => {
  if (index < 0) return 'Unknown';
  return headers[index] || `Column ${index + 1}`;
};

const buildRowColumnError = (rowNumber: number, columnLabel: string, message: string): Error => {
  return new Error(`Row ${rowNumber}, column "${columnLabel}": ${message}`);
};

interface GroupedIndividualIdRows {
  sourceIndividualId: string;
  rows: ParsedSheetRow[];
}

export interface BulkPersonSizingImportPersonResult {
  sourceIndividualId: string;
  normalizedIndividualId: string;
  importedDrafts: Record<string, ImportedSizingDraft>;
  importedCount: number;
}

export interface BulkPersonSizingImportResult {
  people: BulkPersonSizingImportPersonResult[];
  totalPeople: number;
  totalAssignments: number;
}

export const buildBulkPersonSizingImportFromSheet = (
  sheet: PersonSizingImportSheet,
  catalogItems: Entity[],
  individualIdLabel = 'Individual ID',
): BulkPersonSizingImportResult => {
  const individualIdColumn = findIndividualIdColumn(sheet.headers, individualIdLabel);
  if (individualIdColumn < 0) {
    throw new Error(
      `Unable to detect ${individualIdLabel} column. Include an "${individualIdLabel}" column along with the sizing columns.`,
    );
  }

  const groups = new Map<string, GroupedIndividualIdRows>();

  sheet.rows.forEach((row) => {
    const individualIdValue = row.cells[individualIdColumn] ?? '';
    const normalizedIndividualId = normalizeIndividualId(individualIdValue);
    if (!normalizedIndividualId) {
      throw buildRowColumnError(
        row.rowNumber,
        getColumnLabel(sheet.headers, individualIdColumn),
        `Missing ${individualIdLabel} value.`,
      );
    }

    const existing = groups.get(normalizedIndividualId);
    if (existing) {
      existing.rows.push(row);
      return;
    }

    groups.set(normalizedIndividualId, {
      sourceIndividualId: individualIdValue.trim(),
      rows: [row],
    });
  });

  const people = Array.from(groups.entries())
    .map(([normalizedIndividualId, group]) => {
      const personSheet: PersonSizingImportSheet = {
        headers: sheet.headers,
        rows: group.rows,
      };
      const { importedDrafts, importedCount } = buildPersonSizingImportFromSheet({
        sheet: personSheet,
        catalogItems,
      });

      return {
        sourceIndividualId: group.sourceIndividualId || normalizedIndividualId,
        normalizedIndividualId,
        importedDrafts,
        importedCount,
      };
    })
    .sort((a, b) => a.normalizedIndividualId.localeCompare(b.normalizedIndividualId));

  const totalAssignments = people.reduce((sum, person) => sum + person.importedCount, 0);

  return {
    people,
    totalPeople: people.length,
    totalAssignments,
  };
};

export const parseBulkPersonSizingImportFile = async (
  file: File,
  catalogItems: Entity[],
  individualIdLabel = 'Individual ID',
): Promise<BulkPersonSizingImportResult> => {
  const sheet = await parsePersonSizingImportSheet(file);
  return buildBulkPersonSizingImportFromSheet(sheet, catalogItems, individualIdLabel);
};
